import { NgClass, NgTemplateOutlet } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { booleanAttribute, Component, computed, DestroyRef, inject, input, OnDestroy, OnInit, output, Signal, signal, TemplateRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { startWith } from 'rxjs';

import { AppMaterialModule } from '../../../material.module';
import { AttributeDirective } from 'src/app/infrastructure/directives/attribute.directive';
import { _MatFormFieldComponent } from '../../components/_mat-form-field.component';
import { autocomplete, IAutoComplete } from '../../models/auto-complete.model';

// Tekstveld met suggesties (mat-autocomplete). De control bevat de getypte tekst (vrije invoer blijft
// mogelijk); een gekozen suggestie zet de tekst op fieldName_display en vuurt optionSelected met het
// hele item. Lijst en veldnamen zoals bij app-select-control (IAutoComplete of eigen objecten).
@Component({
    selector: 'app-autocomplete-control',
    templateUrl: './autocomplete-control.component.html',
    styleUrls: [
        './autocomplete-control.component.scss',
        '../../control.scss',
    ],
    standalone: true,
    host: {
        '[class.readonly]': 'readonly$$()'
    },
    imports: [
        AppMaterialModule,
        AttributeDirective,
        NgClass,
        NgTemplateOutlet,
        ReactiveFormsModule,
    ],
})
export class AutocompleteControlComponent
    extends _MatFormFieldComponent
    implements OnInit, OnDestroy
{
    private readonly __destroyRef = inject(DestroyRef);

    override attributes$$        = computed(() => ({
        ...this._input_attributes$$(),
        'autocomplete': 'off',
    }));
    override autofocus$$         = this._input_autofocus$$;
    override control$$           = this._control$$;
    override controlName$$       = this._input_controlName$$;
    override disabled$$          = this._input_disabled$$;
    override errorShow$$         = this._input_errorShow$$;
    override errorStateMatcher$$ = this._input_errorStateMatcher$$;
    override id$$                = this._input_id$$;
    override lockOnLoad$$        = this._input_lockOnLoad$$;
    override placeholder$$       = this._input_placeholder$$;
    override readonly$$          = this._input_readonly$$;
    override subscriptHide$$     = this._input_subscriptHide$$;
    override tabIndex$$          = this._tabIndex$$;
    override value$$             = this._input_value$$;
    override valueReset$$        = this._input_valueReset$$;

    // mat-form-field:
    override appearance$$         = this._input_appearance$$;
    override classHeight$$        = this._input_classHeight$$;
    override color$$              = this._input_color$$;
    override floatLabel$$         = this._input_floatLabel$$;
    override hideLineRipple$$     = this._input_hideLineRipple$$;
    override hideRequiredMarker$$ = this._hideRequiredMarker$$;
    override hint$$               = this._input_hint$$;
    override hintAlign$$          = this._input_hintAlign$$;
    override hintLabel$$          = this._input_hintLabel$$;
    override label$$              = this._input_label$$;
    override required$$: Signal<boolean> = this._input_required$$;
    override subscriptSizing$$    = this._input_subscriptSizing$$;

    // mat-autocomplete:
    input_autoActiveFirstOption$$ = input(false,                                     { transform: booleanAttribute, alias: 'autoActiveFirstOption' });
    input_fieldName_display$$     = input<string>(autocomplete.itemText,             { alias: 'fieldName_display' });
    input_fieldName_value$$       = input<string>(autocomplete.itemId,               { alias: 'fieldName_value' });
    // Extra velden waarin het filter ook zoekt (bv. straat, plaats); fieldName_display altijd.
    input_filterFields$$          = input<string[]>([],                              { alias: 'filterFields' });
    input_list$$                  = input<IAutoComplete[] | any[]>([],               { alias: 'list' });
    // Na een keuze het veld leegmaken (kiezen-en-toevoegen, bv. chips): mat-autocomplete schrijft de optietekst pas
    // ná onSelectionChange in de control, dus een setValue(null) van de aanroeper daar wordt overschreven.
    input_clearOnSelect$$         = input(false,                                    { transform: booleanAttribute, alias: 'clearOnSelect' });
    // Eigen weergave van een suggestie (context: $implicit = het item); zonder template de weergavetekst.
    input_optionTemplate$$        = input<TemplateRef<any> | null>(null,             { alias: 'optionTemplate' });
    input_panelWidth$$            = input<string | number | null>(null,              { alias: 'panelWidth' });

    // Knop rechts in het veld (zoals bij app-input-control), bv. om een keuzedialoog te openen.
    input_suffixIcon$$            = input<string | null>(null,                       { alias: 'suffixIcon' });
    input_suffixTooltip$$         = input<string | null>(null,                       { alias: 'suffixTooltip' });

    out_optionSelected = output<any>({ alias: 'optionSelected' });
    out_suffixClick    = output<void>({ alias: 'suffixClick' });

    private readonly __text$$ = signal<string>('');

    fieldNameDisplay$$ = computed(() => this.input_fieldName_display$$() || autocomplete.itemText);
    fieldNameValue$$ = computed(() => this.input_fieldName_value$$() || autocomplete.itemId);

    // Hoofdletterongevoelig op een deel van de tekst, over de weergavetekst en filterFields.
    filtered$$ = computed(() => {
        const __list = this.input_list$$() ?? [];
        const __filter = this.__text$$().trim().toLocaleLowerCase();
        if (!__filter) return __list;

        const __fields = [this.fieldNameDisplay$$(), ...this.input_filterFields$$()];
        return __list.filter(item => __fields.some(field => String(item?.[field] ?? '').toLocaleLowerCase().includes(__filter)));
    });

    override ngOnInit(): void {
        super._ngOnInit();

        const __control = this.control$$();
        __control.valueChanges.pipe(
            startWith(__control.value),
            takeUntilDestroyed(this.__destroyRef),
        ).subscribe(value => this.__text$$.set(value ?? ''));
    }

    // De optie-waarde is de weergavetekst (de control blijft tekst); het item gaat via de output.
    emit_optionSelected(item: any, isUserInput: boolean): void {
        if (!isUserInput) return;
        this.out_optionSelected.emit(item);
    }

    // (optionSelected) van mat-autocomplete: vuurt nadat de optietekst in de control staat.
    emit_afterSelect(): void {
        if (this.input_clearOnSelect$$()) this.control$$().setValue(null);
    }

    emit_suffixClick(event: MouseEvent): void {
        event.stopPropagation();
        this.out_suffixClick.emit();
    }

    itemText(item: any): string {
        return item?.[this.fieldNameDisplay$$()] ?? '';
    }

    trackValue(item: any): string {
        return item?.[this.fieldNameValue$$()];
    }

    override errorMessage_getFirst(): string {
        return this._validationMessage(this.control$$()?.errors);
    }

    override ngOnDestroy(): void {
        super._ngOnDestroy();
    }
}
